import { useCreateBriefCampaignDataFields } from "@entities/brief/lib/hooks/campaigns/useCreateBriefCampaignDataFields";
import { useGetBriefCampaignDataFields } from "@entities/brief/lib/hooks/campaigns/useGetBriefCampaignDataFields";
import { useToggleBriefCampaignDataFieldV2 } from "@entities/brief/lib/hooks/campaigns/useToggleBriefCampaignDataFieldV2";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ToastService } from "@shared/lib/services";
import { CustomFieldType, IFieldConfig } from "@shared/lib/types/briefInfluencerV2";
import { Button, Input, InputNumber } from "antd";
import { Eye, EyeOff, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";

export function CustomizeCampaignColumns({ onClose }: { onClose: () => void }) {
  const { briefId, brief } = useBriefStore();

  const [state, setState] = useState<"view" | "create">("view");

  // Form states for creating custom field
  const [fieldName, setFieldName] = useState("");
  const [fieldType, setFieldType] = useState<CustomFieldType>(CustomFieldType.STRING);
  const [fieldDescription, setFieldDescription] = useState("");
  const [fieldDefault, setFieldDefault] = useState<string | number | undefined>(undefined);

  const beDataFields =
    brief?.visibileOperationsCustomFieldsV2?.filter((f) => !f.isSpecialField) ||
    [];
  const beCustomFields =
    brief?.visibileOperationsCustomFieldsV2?.filter((f) => f.isSpecialField) ||
    [];

  const [selectedDataFields, setSelectedDataFields] =
    useState<IFieldConfig[]>(beDataFields);
  const [selectedCustomFields, setSelectedCustomFields] =
    useState<IFieldConfig[]>(beCustomFields);

  const { dataFields, isFetching } = useGetBriefCampaignDataFields(briefId);

  const [searchInput, setSearchInput] = useState("");
  const searchedInput = searchInput.toLowerCase();

  const { createCampaignDataFields, isCreated, isCreating } =
    useCreateBriefCampaignDataFields(briefId);
  const { isToggled, isToggling, toggleDataField } =
    useToggleBriefCampaignDataFieldV2(briefId);

  // Sync state with brief prop updates
  useEffect(() => {
    if (brief) {
      setSelectedDataFields(
        brief?.visibileOperationsCustomFieldsV2?.filter(
          (f) => !f.isSpecialField
        ) || []
      );
      setSelectedCustomFields(
        brief?.visibileOperationsCustomFieldsV2?.filter(
          (f) => f.isSpecialField
        ) || []
      );
    }
  }, [brief]);

  // Handle successful creation of custom field
  useEffect(() => {
    if (isCreated) {
      ToastService.success("Custom field created successfully");
      setState("view");
      // Reset form fields
      setFieldName("");
      setFieldType(CustomFieldType.STRING);
      setFieldDescription("");
      setFieldDefault(undefined);
    }
  }, [isCreated]);

  // Handle successful save of toggled configurations
  useEffect(() => {
    if (isToggled) {
      ToastService.success("Fields Configured Successfully");
      onClose();
    }
  }, [isToggled]);

  // Select / Deselect Category logic
  const getCategoryMethods = (category: string) => {
    const fieldsInCategory = dataFields?.[category] || [];
    if (fieldsInCategory.length === 0) {
      return {
        isAllSelected: false,
        handleToggle: () => {},
      };
    }

    const isAllSelected = fieldsInCategory.every((f) =>
      selectedDataFields.some((sf) => sf.key === f.key)
    );

    const handleToggle = () => {
      if (isAllSelected) {
        setSelectedDataFields((prev) =>
          prev.filter((f) => f.category !== category)
        );
      } else {
        setSelectedDataFields((prev) => {
          const filteredPrev = prev.filter((f) => f.category !== category);
          return [...filteredPrev, ...fieldsInCategory];
        });
      }
    };

    return { isAllSelected, handleToggle };
  };

  const handleToggleField = (field: IFieldConfig, isCustom: boolean) => {
    if (isCustom) {
      setSelectedCustomFields((prev) => {
        const isAlreadySelected = prev.some((f) => f.key === field.key);
        if (isAlreadySelected) {
          return prev.filter((f) => f.key !== field.key);
        } else {
          return [...prev, field];
        }
      });
    } else {
      setSelectedDataFields((prev) => {
        const isAlreadySelected = prev.some((f) => f.key === field.key);
        if (isAlreadySelected) {
          return prev.filter((f) => f.key !== field.key);
        } else {
          return [...prev, field];
        }
      });
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fieldName.trim()) {
      ToastService.error("Field name is required");
      return;
    }

    createCampaignDataFields({
      briefId,
      operationsCustomFieldsV2: {
        displayName: fieldName,
        type: fieldType,
        description: fieldDescription,
        defaultValue: fieldDefault,
        isSpecialField: true,
        key: fieldName.toLowerCase().replace(/[ .]/g, "_"),
      },
    });
  };

  if (isFetching) {
    return <div className={styles.loading}>Loading campaign fields...</div>;
  }

  return (
    <div className={styles.container}>
      {state === "view" ? (
        <>
          {/* <Input
            placeholder="Search columns"
            value={searchInput}
            size="large"
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
            allowClear
          /> */}

          <div className={styles.viewContainer}>
            {Object.entries(dataFields || {}).map(([categoryName, fields]) => {
              if (categoryName === "Custom Fields") return null;

              const filteredFields = fields.filter((f) =>
                f.displayName?.toLowerCase().includes(searchedInput)
              );

              if (filteredFields.length === 0) return null;

              const { isAllSelected, handleToggle } = getCategoryMethods(categoryName);

              return (
                <div key={categoryName} className={styles.categorySection}>
                  <div className={styles.categoryHeader}>
                    <span className={styles.categoryTitle}>{categoryName}</span>
                    {!searchInput && (
                      <button
                        type="button"
                        onClick={handleToggle}
                        className={styles.selectAllButton}
                      >
                        {isAllSelected ? "Deselect All" : "Select All"}
                      </button>
                    )}
                  </div>
                  <div className={styles.fieldsList}>
                    {filteredFields.map((field) => {
                      const isSelected = selectedDataFields.some(
                        (f) => f.key === field.key
                      );
                      return (
                        <div key={field._id || field.key} className={styles.fieldItem}>
                          <div className={styles.fieldInfo}>
                            <div className={styles.fieldName}>
                              {field.displayName || field.key}
                            </div>
                            {field.description && (
                              <div className={styles.fieldMeta}>
                                <span>{field.description}</span>
                              </div>
                            )}
                          </div>
                          <div className={styles.fieldActions}>
                            <button
                              type="button"
                              onClick={() => handleToggleField(field, false)}
                              className={`${styles.visibilityButton} ${
                                isSelected ? styles.visibilityButtonActive : ""
                              }`}
                              title={isSelected ? "Visible" : "Hidden"}
                            >
                              {isSelected ? <Eye size={14} /> : <EyeOff size={14} />}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Custom Fields Category */}
            {(() => {
              const customFields = dataFields?.["Custom Fields"] || [];
              const filteredCustomFields = customFields.filter((f) =>
                f.displayName?.toLowerCase().includes(searchedInput)
              );

              if (filteredCustomFields.length === 0 && searchInput) return null;

              return (
                <div className={styles.categorySection}>
                  {/* <div className={styles.categoryHeader}>
                    <span className={styles.categoryTitle}>Custom Fields</span>
                  </div> */}

                  {!searchInput && (
                    <button
                      type="button"
                      onClick={() => setState("create")}
                      className={styles.createButton}
                    >
                      <Plus size={16} /> Create Custom Column
                    </button>
                  )}

                  <div className={styles.fieldsList}>
                    {filteredCustomFields.length === 0 ? (
                      <div className={styles.emptyState}>No custom fields yet.</div>
                    ) : (
                      filteredCustomFields.map((field) => {
                        const isSelected = selectedCustomFields.some(
                          (f) => f.key === field.key
                        );
                        return (
                          <div key={field._id || field.key} className={styles.fieldItem}>
                            <div className={styles.fieldInfo}>
                              <div className={styles.fieldName}>
                                {field.displayName || field.key}
                              </div>
                              <div className={styles.fieldMeta}>
                                <span className={styles.fieldType}>
                                  {field.type || "string"}
                                </span>
                                {field.defaultValue && (
                                  <span className={styles.fieldDefault}>
                                    • Default: {String(field.defaultValue)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className={styles.fieldActions}>
                              <button
                                type="button"
                                onClick={() => handleToggleField(field, true)}
                                className={`${styles.visibilityButton} ${
                                  isSelected ? styles.visibilityButtonActive : ""
                                }`}
                                title={isSelected ? "Visible" : "Hidden"}
                              >
                                {isSelected ? <Eye size={14} /> : <EyeOff size={14} />}
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          <div className={styles.footerActions}>
            <Button onClick={onClose} className={styles.cancelButton}>
              Close
            </Button>
            <Button
              type="primary"
              loading={isToggling}
              onClick={() => {
                toggleDataField({
                  briefId,
                  visibileOperationsCustomFieldsV2: [
                    ...selectedDataFields,
                    ...selectedCustomFields,
                  ],
                  brandId: brief?.brandId || "",
                });
              }}
              className={styles.submitButton}
            >
              Save
            </Button>
          </div>
        </>
      ) : (
        <form onSubmit={handleFormSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Field Name</label>
            <Input
              type="text"
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
              placeholder="e.g. Creator Cost Override"
              className={styles.input}
              required
              autoFocus
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Data Type</label>
            <div className={styles.typeGrid}>
              <button
                type="button"
                onClick={() => {
                  setFieldType(CustomFieldType.STRING);
                  setFieldDefault(undefined);
                }}
                className={`${styles.typeButton} ${
                  fieldType === CustomFieldType.STRING ? styles.typeButtonActive : ""
                }`}
              >
                Text
              </button>
              <button
                type="button"
                onClick={() => {
                  setFieldType(CustomFieldType.NUMBER);
                  setFieldDefault(undefined);
                }}
                className={`${styles.typeButton} ${
                  fieldType === CustomFieldType.NUMBER ? styles.typeButtonActive : ""
                }`}
              >
                Number
              </button>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Description (Optional)</label>
            <Input.TextArea
              value={fieldDescription}
              onChange={(e) => setFieldDescription(e.target.value)}
              placeholder="Provide a description for this column..."
              rows={3}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Default Value</label>
            {fieldType === CustomFieldType.NUMBER ? (
              <InputNumber
                value={typeof fieldDefault === "number" ? fieldDefault : undefined}
                onChange={(value) => setFieldDefault(value ?? undefined)}
                placeholder="Value for existing rows"
                className={styles.input}
                required
              />
            ) : (
              <Input
                type="text"
                value={typeof fieldDefault === "string" ? fieldDefault : ""}
                onChange={(e) => setFieldDefault(e.target.value)}
                placeholder="Value for existing rows"
                className={styles.input}
                required
              />
            )}
          </div>

          <div className={styles.formActions}>
            <Button onClick={() => setState("view")} className={styles.cancelButton}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isCreating}
              className={styles.submitButton}
            >
              Create Field
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
